import { useState, useEffect, useRef } from 'react';
import { useAuthFetch } from '../../hooks/useAuthFetch';

function EditModal({ question, onSave, onClose }) {
  const { authFetch } = useAuthFetch();
  const [form, setForm] = useState({
    correct_answer:   question.correct_answer   ?? '',
    docs_url:         question.docs_url         ?? '',
    importance:       question.importance       ?? '',
    difficulty:       question.difficulty       ?? '',
    youtube_link:     question.youtube_link     ?? '',
    youtube_keywords: question.youtube_keywords ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        correct_answer: form.correct_answer.trim(),
        docs_url:       form.docs_url     || null,
        importance:     form.importance   !== '' ? parseInt(form.importance,  10) : null,
        difficulty:     form.difficulty   !== '' ? parseInt(form.difficulty,  10) : null,
        youtube_link:     form.youtube_link     || null,
        youtube_keywords: form.youtube_keywords || null,
      };
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/questions/${question.id}`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Server error ${res.status}`);
      }
      const updated = await res.json();
      onSave(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  const inputStyle = {
    width: '100%', padding: '0.4rem 0.6rem', borderRadius: '6px',
    border: '1px solid var(--color-border-strong)', background: 'var(--color-surface)',
    color: 'var(--color-text)', fontSize: '0.875rem', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.25rem', display: 'block' };
  const fieldStyle = { marginBottom: '0.85rem' };

  return (
    <div onClick={handleBackdrop} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--color-bg)', borderRadius: '12px',
        border: '1px solid var(--color-border-strong)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        padding: '1.5rem', width: '500px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>Edit Question #{question.id}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)', marginTop: '2px' }}>
              {question.domain_name} · {question.topic_name}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--color-text-muted)', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ ...fieldStyle, background: 'var(--color-surface)', borderRadius: '6px', padding: '0.6rem 0.75rem', fontSize: '0.85rem', color: 'var(--color-text)', marginBottom: '1.25rem', border: '1px solid var(--color-border)' }}>
          {question.question_text}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Correct Answer</label>
          <textarea
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            value={form.correct_answer}
            onChange={e => set('correct_answer', e.target.value)}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Docs URL</label>
          <input style={inputStyle} value={form.docs_url} onChange={e => set('docs_url', e.target.value)} placeholder="https://…" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>YouTube Link</label>
          <input style={inputStyle} value={form.youtube_link} onChange={e => set('youtube_link', e.target.value)} placeholder="https://youtube.com/…" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>YouTube Keywords</label>
          <input style={inputStyle} value={form.youtube_keywords} onChange={e => set('youtube_keywords', e.target.value)} placeholder="e.g. Azure AD conditional access" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.85rem' }}>
          <div>
            <label style={labelStyle}>Importance (1–10)</label>
            <input style={inputStyle} type="number" min="1" max="10" value={form.importance} onChange={e => set('importance', e.target.value)} placeholder="—" />
          </div>
          <div>
            <label style={labelStyle}>Difficulty (1–10)</label>
            <input style={inputStyle} type="number" min="1" max="10" value={form.difficulty} onChange={e => set('difficulty', e.target.value)} placeholder="—" />
          </div>
        </div>

        {error && <p className="costs-error" style={{ marginBottom: '0.75rem' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="costs-range-btn" disabled={saving}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '0.3rem 1rem', borderRadius: '6px', border: '1px solid rgba(249,115,22,0.55)',
            background: 'var(--color-accent-bg-2)', color: 'var(--color-text)', fontSize: '0.85rem',
            fontWeight: 500, cursor: saving ? 'wait' : 'pointer',
          }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

const LIMITS = [10, 25, 50, 100];

const COLUMNS = [
  { key: 'id',             label: '#' },
  { key: 'domain_name',    label: 'Domain · Topic' },
  { key: 'question_text',  label: 'Question' },
  { key: 'difficulty',     label: 'Difficulty' },
  { key: 'importance',     label: 'Importance' },
  { key: 'last_validated', label: 'Last Validated' },
];

function Pagination({ page, total, limit, onPage }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;

  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
  }

  const btnStyle = (active) => ({
    padding: '0.25rem 0.6rem', borderRadius: '5px', fontSize: '0.82rem', fontWeight: 500,
    border: '1px solid', cursor: 'pointer',
    background: active ? 'var(--color-accent-bg-2)' : 'var(--color-surface)',
    color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
    borderColor: active ? 'rgba(249,115,22,0.55)' : 'var(--color-border-strong)',
  });

  return (
    <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <button style={btnStyle(false)} disabled={page === 1} onClick={() => onPage(page - 1)}>Prev</button>
      {pages.map((p, i) =>
        p === '…'
          ? <span key={`e${i}`} style={{ padding: '0 0.2rem', color: 'var(--color-text-subtle)', fontSize: '0.82rem' }}>…</span>
          : <button key={p} style={btnStyle(p === page)} onClick={() => onPage(p)}>{p}</button>
      )}
      <button style={btnStyle(false)} disabled={page === totalPages} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  );
}

function DeficitSection() {
  const { authFetch } = useAuthFetch();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    if (data) { setOpen(true); return; }
    setLoading(true);
    authFetch(`${process.env.REACT_APP_API_URL}/api/admin/questions/deficit`)
      .then(r => r.json())
      .then(d => { setData(d); setOpen(true); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  };

  const toggle = () => {
    if (!open && !data) { load(); return; }
    setOpen(v => !v);
  };

  return (
    <div className="costs-section" style={{ marginBottom: '1rem' }}>
      <div
        onClick={toggle}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
      >
        <span className="costs-section-title" style={{ margin: 0 }}>
          Question Deficit{data ? ` — ${data.totalDeficit.toLocaleString()} total` : ''}
        </span>
        <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
          {loading ? 'Loading…' : open ? '▲ Hide' : '▼ Show'}
        </span>
      </div>

      {error && <p className="costs-error" style={{ marginTop: '0.5rem' }}>{error}</p>}

      {open && data && (
        <div className="costs-table-wrapper" style={{ marginTop: '0.75rem' }}>
          <table className="costs-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Group</th>
                <th>Topic</th>
                <th style={{ textAlign: 'center' }}>Expected</th>
                <th style={{ textAlign: 'center' }}>Have</th>
                <th style={{ textAlign: 'center' }}>Deficit</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ fontSize: '0.82rem', color: 'var(--color-text-subtle)', whiteSpace: 'nowrap' }}>{r.domain_name}</td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--color-text-subtle)', whiteSpace: 'nowrap' }}>{r.group_name}</td>
                  <td style={{ fontSize: '0.85rem' }}>{r.topic_name}</td>
                  <td style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>{r.expected_exam_questions * 2}</td>
                  <td style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>{r.current_questions}</td>
                  <td style={{ textAlign: 'center', fontWeight: 600, color: r.deficit > 100 ? 'var(--color-danger, #ef4444)' : 'var(--color-text)' }}>{r.deficit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function QuestionsTab() {
  const { authFetch } = useAuthFetch();
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [missingOnly, setMissingOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const debounceRef = useRef(null);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 400);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page, limit });
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (sortKey) { params.set('sort', sortKey); params.set('dir', sortDir); }
    if (missingOnly) params.set('missing', 'true');
    authFetch(`${process.env.REACT_APP_API_URL}/api/admin/questions?${params}`)
      .then(res => res.json())
      .then(data => {
        setQuestions(data.questions || []);
        setTotal(data.total || 0);
        setLoading(false);
      })
      .catch(err => { console.error(err); setError('Failed to load questions.'); setLoading(false); });
  }, [page, limit, debouncedSearch, sortKey, sortDir, missingOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = (updated) => {
    setQuestions(prev => prev.map(q => q.id === updated.id ? { ...q, ...updated } : q));
    setEditing(null);
  };

  const handleLimitChange = (newLimit) => { setLimit(newLimit); setPage(1); };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const thStyle = (key) => ({
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    color: sortKey === key ? 'var(--color-text)' : undefined,
  });
  const SortIcon = ({ colKey }) => {
    if (sortKey !== colKey) return <span style={{ opacity: 0.3, marginLeft: '0.3em' }}>↕</span>;
    return <span style={{ marginLeft: '0.3em' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div>
      <DeficitSection />
      {editing && <EditModal question={editing} onSave={handleSave} onClose={() => setEditing(null)} />}

      <div className="costs-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
          <p className="costs-section-title" style={{ margin: 0 }}>
            Questions{total > 0 ? ` (${start}–${end} of ${total})` : ''}
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)', marginRight: '0.25rem' }}>Per page:</span>
              {LIMITS.map(l => (
                <button key={l} onClick={() => handleLimitChange(l)} className="costs-range-btn" style={{
                  background: l === limit ? 'var(--color-accent-bg-2)' : undefined,
                  color: l === limit ? 'var(--color-text)' : undefined,
                  borderColor: l === limit ? 'rgba(249,115,22,0.55)' : undefined,
                  padding: '0.25rem 0.6rem',
                }}>{l}</button>
              ))}
            </div>
            <button
              onClick={() => { setMissingOnly(v => !v); setPage(1); }}
              className="costs-range-btn"
              style={{
                background: missingOnly ? 'var(--color-accent-bg-2)' : undefined,
                color: missingOnly ? 'var(--color-text)' : undefined,
                borderColor: missingOnly ? 'rgba(249,115,22,0.55)' : undefined,
              }}
            >
              Missing values
            </button>
            <input
              type="search"
              placeholder="Search questions, topics…"
              value={search}
              onChange={e => handleSearch(e.target.value)}
              style={{
                padding: '0.3rem 0.7rem', borderRadius: '6px', fontSize: '0.85rem',
                border: '1px solid var(--color-border-strong)', background: 'var(--color-surface)',
                color: 'var(--color-text)', width: '240px',
              }}
            />
          </div>
        </div>

        {error && <p className="costs-error">{error}</p>}

        <div className="costs-table-wrapper" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          <table className="costs-table">
            <thead>
              <tr>
                {COLUMNS.map(col => (
                  <th key={col.key} style={thStyle(col.key)} onClick={() => handleSort(col.key)}>
                    {col.label}<SortIcon colKey={col.key} />
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {questions.map(q => (
                <tr key={q.id}>
                  <td style={{ color: 'var(--color-text-subtle)', fontFamily: 'monospace', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    #{q.id}
                  </td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)', whiteSpace: 'nowrap' }}>
                    {q.domain_name}<br />{q.topic_name}
                  </td>
                  <td style={{ maxWidth: 420 }}>
                    <span title={q.question_text}>
                      {q.question_text.length > 100 ? q.question_text.slice(0, 100) + '…' : q.question_text}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>{q.difficulty ?? '—'}</td>
                  <td style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>{q.importance ?? '—'}</td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)', whiteSpace: 'nowrap' }}>
                    {q.last_validated ? new Date(q.last_validated).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <button onClick={() => setEditing({ ...q })} style={{
                      padding: '2px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 500,
                      border: '1px solid var(--color-border-strong)', background: 'var(--color-surface)',
                      color: 'var(--color-text-muted)', cursor: 'pointer',
                    }}>Edit</button>
                  </td>
                </tr>
              ))}
              {!loading && questions.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--color-text-subtle)', padding: '1.5rem' }}>
                    {debouncedSearch ? 'No questions match your search.' : 'No questions found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
            <Pagination page={page} total={total} limit={limit} onPage={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
